import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { BelegBezahltBody, EigenbelegBody, HelferBeleg, PayoutPatchBody } from "../lib/types";
import { apiDelete, apiGet, apiPatch, apiPost } from "@vimaana/ui";
import type {
  PayoutCreate,
  TeamDashboard,
  TeamMemberDetail,
  TeamMemberPatch,
  TeamMemberProfile,
  TeamOwner,
  TeamOwnerPatch,
  TeamPayout,
  TeamWorkEntry,
  OwnerDetail,
  WorkEntryPatch,
} from "../lib/team";
import type { TeamMemberCreateBody } from "../lib/types";

const teamKey = ["kontor", "team"] as const;

export function useTeamDashboard() {
  return useQuery<TeamDashboard>({
    queryKey: [...teamKey, "dashboard"],
    queryFn: () => apiGet<TeamDashboard>("/api/kontor/team/dashboard"),
    staleTime: 30_000,
  });
}

export function useTeamMemberDetail(memberId: string | undefined, year: number) {
  return useQuery<TeamMemberDetail>({
    queryKey: [...teamKey, "member", memberId, year],
    queryFn: () => apiGet<TeamMemberDetail>(
      `/api/kontor/team/member/${encodeURIComponent(memberId ?? "")}/detail?year=${year}`,
    ),
    enabled: Boolean(memberId),
    staleTime: 15_000,
  });
}

export function useCreateTeamMember() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: TeamMemberCreateBody) => apiPost<TeamMemberProfile>("/api/kontor/team/member", body),
    onSuccess: () => client.invalidateQueries({ queryKey: teamKey }),
  });
}

export function useUpdateTeamMember() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: TeamMemberPatch }) => apiPatch<TeamMemberProfile>(
      `/api/kontor/team/member/${encodeURIComponent(id)}`,
      patch,
    ),
    onSuccess: () => client.invalidateQueries({ queryKey: teamKey }),
  });
}

export function useCreateTeamOwner() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: Pick<TeamOwner, "name" | "phone" | "email">) => apiPost<TeamOwner>(
      "/api/kontor/team/owner",
      body,
    ),
    onSuccess: () => client.invalidateQueries({ queryKey: teamKey }),
  });
}

export function useUpdateTeamOwner() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: TeamOwnerPatch }) => apiPatch<TeamOwner>(
      `/api/kontor/team/owner/${encodeURIComponent(id)}`,
      patch,
    ),
    onSuccess: () => client.invalidateQueries({ queryKey: teamKey }),
  });
}

export function useCreatePayout(memberId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: PayoutCreate) => apiPost<TeamPayout>(
      `/api/kontor/team/member/${encodeURIComponent(memberId)}/payout`,
      body,
    ),
    onSuccess: () => client.invalidateQueries({ queryKey: teamKey }),
  });
}

export function useDeletePayout(memberId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payoutId: string) => apiDelete<{ ok: boolean }>(
      `/api/kontor/team/member/${encodeURIComponent(memberId)}/payout/${encodeURIComponent(payoutId)}`,
    ),
    onSuccess: () => client.invalidateQueries({ queryKey: teamKey }),
  });
}

export function useUpdateWorkEntryStatus(memberId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ slotId, action, grund = "" }: {
      slotId: string;
      action: "confirm" | "reject" | "reopen";
      grund?: string;
    }) => apiPatch<TeamWorkEntry>(
      `/api/kontor/team/member/${encodeURIComponent(memberId)}/work-entry/${encodeURIComponent(slotId)}/status`,
      { action, grund },
    ),
    onSuccess: () => client.invalidateQueries({ queryKey: teamKey }),
  });
}

/** Inhaber-Detail: Stammdaten, Kennzahlen und Einsätze eines Jahres. */
export function useOwnerDetail(ownerId: string | undefined, year: number) {
  return useQuery<OwnerDetail>({
    queryKey: [...teamKey, "owner", ownerId, year],
    queryFn: () => apiGet<OwnerDetail>(`/api/kontor/team/owner/${ownerId}/detail?year=${year}`),
    enabled: !!ownerId,
    staleTime: 30_000,
  });
}

/**
 * Einen Stundeneintrag ändern bzw. löschen. Beides läuft über die schon
 * vorhandenen Slot-Endpunkte — es fehlte nur die Bedienung im Team-Bereich.
 */
export function useUpdateWorkEntry() {
  const qc = useQueryClient();
  const nachziehen = () => {
    qc.invalidateQueries({ queryKey: teamKey });
    qc.invalidateQueries({ queryKey: ["projekte"] });
    qc.invalidateQueries({ queryKey: ["kalender"] });
  };
  return useMutation({
    mutationFn: ({ slotId, patch }: { slotId: string; patch: WorkEntryPatch }) =>
      apiPatch<unknown>(`/api/kontor/slots/${slotId}`, patch),
    onSuccess: nachziehen,
  });
}

export function useDeleteWorkEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slotId: string) => apiDelete<unknown>(`/api/kontor/slots/${slotId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: teamKey });
      qc.invalidateQueries({ queryKey: ["projekte"] });
      qc.invalidateQueries({ queryKey: ["kalender"] });
    },
  });
}

/** Auszahlung aendern — der Server fuehrt die Inhaber-Einlage mit nach. */
export function useUpdatePayout(memberId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ payoutId, patch }: { payoutId: string; patch: PayoutPatchBody }) =>
      apiPatch<unknown>(`/api/kontor/team/member/${memberId}/payout/${payoutId}`, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: teamKey });
      qc.invalidateQueries({ queryKey: ["finanzen"] });
    },
  });
}

/** Belege, die auf das Lohnkonto dieses Helfers einzahlen. */
export function useMemberBelege(memberId: string, year?: number) {
  return useQuery<{ items: HelferBeleg[] }>({
    queryKey: [...teamKey, "belege", memberId, year || 0],
    queryFn: () => apiGet(`/api/kontor/team/member/${memberId}/belege?year=${year || 0}`),
    enabled: Boolean(memberId),
    staleTime: 30_000,
  });
}

/**
 * Eine Helferrechnung als bezahlt buchen.
 *
 * Der Server legt daraus die Auszahlung aufs Lohnkonto an — deshalb muss auch
 * die Finanzen-Ansicht neu laden, nicht nur die Belegliste.
 */
export function usePayBeleg(memberId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ belegId, body }: { belegId: string; body: BelegBezahltBody }) =>
      apiPost<unknown>(`/api/kontor/team/member/${memberId}/beleg/${belegId}/bezahlt`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: teamKey });
      qc.invalidateQueries({ queryKey: ["belege"] });
      qc.invalidateQueries({ queryKey: ["finanzen"] });
    },
  });
}

/**
 * Zahlungsnachweis einer privaten Auszahlung erzeugen und an DATEV senden.
 *
 * Beim Buchen geschieht das von selbst; der Knopf ist fürs Nachsenden nach
 * einem Fehler und für Altbuchungen. Der Server ist idempotent — ein
 * versendeter, aktueller Nachweis geht nicht zweimal raus.
 */
export function useNachweisSenden(memberId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payoutId: string) => apiPost<{ nachweis_status: string; fehler: string }>(
      `/api/kontor/team/member/${encodeURIComponent(memberId)}/payout/${encodeURIComponent(payoutId)}/nachweis`,
      {},
    ),
    onSuccess: () => qc.invalidateQueries({ queryKey: teamKey }),
  });
}

/**
 * Eigenbeleg erzeugen — Ersatzbeleg fuer eine Ausgabe ohne Fremdbeleg.
 * Server erzeugt PDF, legt es in Nextcloud ab und schreibt die beleg-Zeile.
 */
export function useCreateEigenbeleg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: EigenbelegBody) => apiPost<{ id: string }>("/api/kontor/eigenbeleg", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: teamKey });
      qc.invalidateQueries({ queryKey: ["belege"] });
    },
  });
}
